import { useState } from "react";

interface RetirableRow {
  isActive: boolean;
}

/**
 * The "hide retired" split every Settings → Config list makes.
 *
 * The API sorts inactive rows last, so the active ones are always the prefix
 * and anything retired is always a suffix — which is what keeps the reorder
 * arrows adjacent swaps rather than swaps with a hidden neighbour. Four lists
 * each carried their own copy of that reasoning; it belongs in one place,
 * because it is a property of the API's ordering rather than of any one list.
 *
 * `active.length` is what a page's last-row check must compare against, not
 * `visible.length`: with retired rows shown, the last *active* row is still the
 * last one that can move down.
 */
export function useRetiredRows<T extends RetirableRow>(rows: T[]) {
  const [hideRetired, setHideRetired] = useState(true);

  const active = rows.filter((r) => r.isActive);

  return {
    hideRetired,
    setHideRetired,
    /** Only the rows still offered. */
    active,
    /** How many are retired — the count the filter's label shows. */
    hiddenCount: rows.length - active.length,
    /** What the table renders. */
    visible: hideRetired ? active : rows,
  };
}
